import {
  formatRegisterDobForApi,
  normalizeRegisterDobDisplayValue,
} from "../Register/Register.validator";
import { buildCoverageResultFromEligibility } from "./Insurance/registrationInsuranceMock";
import { normalizeVerificationRelationship } from "./Insurance/insuranceProviders";
import { updateRegistrationData } from "./registrationSetupStorage";

const normalizePaymentType = (value) => {
  if (!value) return null;
  const normalized = String(value).toLowerCase();
  if (normalized === "insurance" || normalized === "selfpay") {
    return normalized;
  }
  return null;
};

export const hasCheckEmailData = (res) => {
  const data = res?.data;
  if (data == null) {
    return false;
  }
  if (Array.isArray(data)) {
    return data.length > 0;
  }
  if (typeof data === "object") {
    return Object.keys(data).length > 0;
  }
  return Boolean(data);
};

export const isCheckEmailSuccess = (res) => Number(res?.status) === 1;

export const isCheckEmailNotFound = (res) =>
  !hasCheckEmailData(res) && !isCheckEmailSuccess(res);

export const getCheckEmailDisplayMessage = (res, fallback = "") =>
  res?.message || res?.data?.message || fallback;

export const buildCheckEmailAndCodePayload = (values) => ({
  email: String(values.email || "").trim(),
  registration_code: String(values.registrationCode || "").toUpperCase().trim(),
  first_name: String(values.firstName || "").trim(),
  last_name: String(values.lastName || "").trim(),
  preferred_name: String(values.preferredName || "").trim(),
  zipcode: String(values.zipCode || "").trim(),
  dob: formatRegisterDobForApi(values.dob),
  category_id: Number(values.categoryId),
  password: String(values.password || ""),
  isCheckedAcknowledgements: Boolean(
    values.isCheckedAcknowledgements ?? values.acceptedTerms
  ),
});

const isTruthyApiFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const isFalsyApiFlag = (value) =>
  value === false || value === 0 || value === "0" || value === "false";

export const isCheckEmailCompletedAccountNoResume = (res) => {
  const data = res?.data ?? {};
  return (
    isCheckEmailSuccess(res) &&
    isTruthyApiFlag(data.account_completed) &&
    isFalsyApiFlag(data.can_resume)
  );
};

export const canContinueFromEmailCodeCheck = (res) =>
  isTruthyApiFlag(res?.data?.can_resume);

export const getProviderAvailableFromEmailCodeCheck = (res) => {
  const data = res?.data ?? {};
  if (
    data &&
    typeof data === "object" &&
    Object.prototype.hasOwnProperty.call(data, "provider_available")
  ) {
    return data.provider_available;
  }
  if (res && Object.prototype.hasOwnProperty.call(res, "provider_available")) {
    return res.provider_available;
  }
  return undefined;
};

export const isProviderUnavailableFromEmailCodeCheck = (res) =>
  isFalsyApiFlag(getProviderAvailableFromEmailCodeCheck(res));

export const shouldShowProviderUnavailableScreen = (res) => {
  const data = res?.data ?? {};
  if (!isFalsyApiFlag(data.provider_available)) {
    return false;
  }
  if (isTruthyApiFlag(data.provider_availability_checked)) {
    return true;
  }
  return isCheckEmailSuccess(res);
};

export const buildRdAvailabilityFromEmailCodeCheck = (res) => {
  if (!shouldShowProviderUnavailableScreen(res)) {
    return null;
  }

  const data = res?.data ?? {};

  return {
    is_rd_available: false,
    provider_available: false,
    provider_availability_checked: true,
    state_id: data?.state_id ?? null,
    state_name: data?.state_name ?? null,
    state_code: data?.state_code ?? null,
    reference_number:
      data?.reference_number ?? data?.reference_no ?? data?.referenceNumber ?? null,
  };
};

export const isCheckEmailResumeEligible = (res) =>
  isCheckEmailSuccess(res) &&
  isTruthyApiFlag(res?.data?.can_resume) &&
  !isCheckEmailCompletedAccountNoResume(res);

export const isCheckEmailCannotResume = (res) =>
  hasCheckEmailData(res) && isFalsyApiFlag(res?.data?.can_resume);

const mapSelectedCategoryFromApi = (item) => {
  const id = item?.id ?? item?.category_id;
  if (id == null) {
    return null;
  }

  return {
    id: Number(id),
    title: String(item?.name ?? item?.title ?? "").trim(),
    description: String(item?.description ?? item?.subtitle ?? "").trim(),
    image: item?.image ?? null,
  };
};

export const extractSelectedCategoriesFromCheckEmail = (data, user, progress) => {
  const raw =
    data?.selected_categories ??
    progress?.selected_categories ??
    user?.selected_categories;

  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.map(mapSelectedCategoryFromApi).filter(Boolean);
};

export const resolvePrefilledCategoryId = (
  selectedCategories,
  serviceIdFallback
) => {
  const firstId = selectedCategories[0]?.id;
  if (firstId != null) {
    return firstId;
  }

  if (serviceIdFallback !== "" && serviceIdFallback != null) {
    return Number(serviceIdFallback);
  }

  return "";
};

export const mergePrefilledCategoryOptions = (existingOptions, selectedCategories) => {
  if (!selectedCategories?.length) {
    return existingOptions;
  }

  const merged = [...existingOptions];
  selectedCategories.forEach((prefilled) => {
    const index = merged.findIndex((option) => option.id === prefilled.id);
    if (index >= 0) {
      merged[index] = {
        ...merged[index],
        title: prefilled.title || merged[index].title,
        description: prefilled.description || merged[index].description,
      };
    } else {
      merged.push(prefilled);
    }
  });

  return merged;
};

const pickFirstNonEmpty = (...values) => {
  for (const value of values) {
    if (value == null) {
      continue;
    }
    const asString = String(value).trim();
    if (asString !== "" && asString !== "null" && asString !== "undefined") {
      return value;
    }
  }
  return "";
};

const hasObjectKeys = (value) =>
  value != null && typeof value === "object" && Object.keys(value).length > 0;

/** Map resume API insurance payloads into registration storage shape. */
export const mapInsuranceFieldsFromCheckEmailProgress = (
  progress = {},
  user = {},
  data = {}
) => {
  const insuranceData =
    (hasObjectKeys(progress.insurance_data) && progress.insurance_data) ||
    (hasObjectKeys(data.insurance_data) && data.insurance_data) ||
    {};
  const manualInsurance =
    (hasObjectKeys(progress.manual_insurance) && progress.manual_insurance) ||
    (hasObjectKeys(data.manual_insurance) && data.manual_insurance) ||
    {};
  const eligibilitySnapshot =
    (hasObjectKeys(progress.stedi_eligibility_snapshot) &&
      progress.stedi_eligibility_snapshot) ||
    (hasObjectKeys(data.stedi_eligibility_snapshot) &&
      data.stedi_eligibility_snapshot) ||
    null;

  if (
    !hasObjectKeys(insuranceData) &&
    !hasObjectKeys(manualInsurance) &&
    !eligibilitySnapshot
  ) {
    return {};
  }

  const insuranceId = pickFirstNonEmpty(
    insuranceData.insurance_id,
    manualInsurance.insurance_id,
    eligibilitySnapshot?.insurance_id
  );
  const memberId = pickFirstNonEmpty(
    insuranceData.member_id,
    manualInsurance.member_id,
    eligibilitySnapshot?.member_id
  );
  const groupNumber = pickFirstNonEmpty(
    insuranceData.group_number,
    manualInsurance.group_number,
    manualInsurance.group_id,
    eligibilitySnapshot?.group_number
  );
  const providerName = pickFirstNonEmpty(
    insuranceData.payer_name,
    eligibilitySnapshot?.insurance_name,
    eligibilitySnapshot?.payer_name,
    eligibilitySnapshot?.stedi_payer_name
  );
  const relationship =
    normalizeVerificationRelationship(
      pickFirstNonEmpty(
        insuranceData.relationship_to_policy_holder,
        manualInsurance.is_primary_subscriber,
        manualInsurance.relationship
      )
    ) || "self";

  const payer = insuranceId !== "" ? String(insuranceId) : "";
  const memberIdValue = memberId !== "" ? String(memberId).trim() : "";
  const groupNumberValue =
    groupNumber !== "" ? String(groupNumber).trim() : "";

  const insuranceVerification = {
    payer,
    providerName: providerName !== "" ? String(providerName).trim() : "",
    memberId: memberIdValue,
    groupNumber: groupNumberValue,
    firstName: String(
      pickFirstNonEmpty(
        insuranceData.first_name,
        user.first_name,
        progress.first_name
      ) || ""
    ).trim(),
    lastName: String(
      pickFirstNonEmpty(
        insuranceData.last_name,
        user.last_name,
        progress.last_name
      ) || ""
    ).trim(),
    dob: String(
      pickFirstNonEmpty(insuranceData.dob, user.dob, progress.dob) || ""
    ).trim(),
    relationship,
  };

  const insuranceManual = {
    provider: payer || (providerName !== "" ? String(providerName).trim() : ""),
    memberId: memberIdValue,
    groupNumber: groupNumberValue,
    relationship,
    holderFirstName: String(
      pickFirstNonEmpty(
        insuranceData.policy_holder_first_name,
        insuranceData.subscriber_first_name,
        manualInsurance.primary_subscriber_first_name
      ) || ""
    ).trim(),
    holderLastName: String(
      pickFirstNonEmpty(
        insuranceData.policy_holder_last_name,
        insuranceData.subscriber_last_name,
        manualInsurance.primary_subscriber_last_name
      ) || ""
    ).trim(),
    holderDob: String(
      pickFirstNonEmpty(
        insuranceData.policy_holder_dob,
        insuranceData.subscriber_dob,
        manualInsurance.primary_subscriber_dob
      ) || ""
    ).trim(),
  };

  const mapped = {
    insuranceVerification,
    insuranceManual,
  };

  if (eligibilitySnapshot) {
    mapped.insuranceEligibilityResult = eligibilitySnapshot;
    const coverageResult = buildCoverageResultFromEligibility(
      eligibilitySnapshot,
      insuranceVerification,
      insuranceManual,
      []
    );
    if (coverageResult) {
      mapped.coverageResult = coverageResult;
    }
  }

  return mapped;
};

export const mapCheckEmailResponseToRegistrationData = (res, email) => {
  const data = res?.data ?? {};
  const user = data.user ?? {};
  const progress = data.registration_progress ?? {};
  const selectedCategories = extractSelectedCategoriesFromCheckEmail(
    data,
    user,
    progress
  );
  const serviceIdFallback =
    data.service_id ?? progress.service_id ?? user.service_id ?? "";
  const dobSource = pickFirstNonEmpty(user.dob, progress.dob);
  const zipSource = pickFirstNonEmpty(user.zipcode, progress.zipcode);

  return {
    firstName: user.first_name || progress.first_name || "",
    lastName: user.last_name || progress.last_name || "",
    preferredName: user.preferred_name || "",
    email: user.email || email || "",
    registrationCode: String(
      data.registration_code || progress.registration_code || ""
    )
      .toUpperCase()
      .trim(),
    dob: dobSource ? normalizeRegisterDobDisplayValue(dobSource) : "",
    zipCode: zipSource ? String(zipSource).trim() : "",
    selectedCategories,
    categoryId: resolvePrefilledCategoryId(selectedCategories, serviceIdFallback),
    paymentType: normalizePaymentType(
      data.payment_type ?? progress.payment_type ?? user.payment_type
    ),
    canResume: Boolean(data.can_resume),
    resumeStep: data.resume_step ?? user.registration_current_step ?? null,
    registrationStatus:
      data.registration_status ?? user.registration_status ?? null,
    isCardPresent: Boolean(
      data.is_card_present ??
        progress.is_card_present ??
        user.is_card_present ??
        false
    ),
    isAppointmentBooked: Boolean(
      data.is_appointment_booked ?? user.is_appointment_booked ?? false
    ),
    userId: data.user_id ?? user.id ?? null,
    accountCompleted: Boolean(data.account_completed),
    emailCheckResponse: data,
    ...mapInsuranceFieldsFromCheckEmailProgress(progress, user, data),
  };
};

export const applyCheckEmailResumePrefill = ({
  res,
  email,
  setFieldValue,
  validateForm,
  onRegistrationCodeLocked,
}) => {
  if (isCheckEmailCompletedAccountNoResume(res)) {
    return { email: String(email || "").trim() };
  }

  const registrationData = mapCheckEmailResponseToRegistrationData(res, email);
  updateRegistrationData(registrationData);

  if (registrationData.firstName) {
    setFieldValue("firstName", registrationData.firstName);
  }
  if (registrationData.lastName) {
    setFieldValue("lastName", registrationData.lastName);
  }
  if (registrationData.preferredName) {
    setFieldValue("preferredName", registrationData.preferredName);
  }
  if (registrationData.registrationCode) {
    setFieldValue("registrationCode", registrationData.registrationCode);
    onRegistrationCodeLocked?.(true);
  }
  if (registrationData.dob) {
    setFieldValue("dob", registrationData.dob);
  }
  if (registrationData.zipCode) {
    setFieldValue("zipCode", registrationData.zipCode);
  }
  if (
    registrationData.categoryId !== "" &&
    registrationData.categoryId != null
  ) {
    setFieldValue("categoryId", Number(registrationData.categoryId));
  }

  validateForm?.();
  return registrationData;
};
